/**
 * API is responsible to all logic related to endpoints.
 * It is responsible to get errors returned from endpoints in different formats and throw a single error message.
 * TODO: throw an object with message and optionally a code or other relevant information.
 */
import axios from "axios";
import {
  getHttpProtocol as scheme,
  getUserServiceUrl as userServiceUrl,
} from "../utils/requests";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";

// custom axios
const userAxios = axios.create({
  baseURL: `${scheme}//${userServiceUrl}`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

userAxios.interceptors.request.use(requestAccessTokenInterceptor);

userAxios.interceptors.request.use(refreshAccessTokenInterceptor);

userAxios.interceptors.response.use(responseStatusInterceptor);

// entities types
export interface Member {
  name: string;
  value: string;
  id: string;
  user_id: number;
  client_uuid: string;
  first_name: string;
  last_name: string;
  phone: string;
  nickname: string;
  user_type_id: number;
  is_lead: number;
  is_member: number;
  email: string;
  uuid: string;
  active: number;
  last_checkin_timestamp: number;
  membership_status: string;
  user_uuid: string;
  image_url: string;
  primary_image_key: string;
  default_billing_id?: string;
  billing_last_four?: string;
  billable: string;
  subscription_count: number;
  headshot: string;
}

export interface V2Member {
  clientUserId: number;
  clientUuid: string;
  uuid: string;
  userUuid: string;
  assignedToUserId: number;
  parentUserId: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  phone: string;
  nickname: string;
  status: string;
  dob?: string;
  externalProfileUrl: string;
  objective?: string;
  isLead: boolean;
  isMember: boolean;
  isStaff: boolean;
  active: boolean;
  archived: boolean;
  deleted: boolean;
  primaryImage: object;
  createdAt: string;
  updatedAt: string;
  joinedAt: string;
  lastCheckedInAt: string;
  activatedAt: string;
  membershipStatus: string;
  membershipRole: string;
  memberSince?: string;
  membershipPaused: number;
  code: string;
  display: string;
  paused: boolean;
  isSynced: boolean;
}

export type QuickActionType = "link" | "legacy_link" | "external_link" | "callback";

export type QuickActionValue =
  | "REPORT_PAYMENTS_FORECAST"
  | "REPORT_FINANCIAL"
  | "REPORT_NO_SHOW"
  | "REPORT_ENGAGEMENT"
  | "REPORT_CELEBRATIONS"
  | "MEMBERS"
  | "LEADS"
  | "STAFF"
  | "CHECKIN_SIDE_PANEL";

export type QuickActionRole =
  | "superuser"
  | "admin"
  | "coach"
  | "frontdesk"
  | "member"
  | "non-member"
  | "ex-member"
  | "lead";
export interface QuickAction {
  uuid: string;
  value: QuickActionValue;
  label: string;
  action: string;
  description: string;
  type: QuickActionType;
  role: QuickActionRole;
  position: number;
  isDefault?: boolean;
}

type QuickActionBatchUpdateResult = {
  action: string;
  status: string;
  result: { numUpdated: string | number; numCreated: string | number }[];
  normalizedPosition: number;
};

export type QuickActionBatchUpdateResponse = {
  data: string;
  count: number;
  results: QuickActionBatchUpdateResult[];
};

export type DatePresetType =
  | "today"
  | "yesterday"
  | "week_to_date"
  | "month_to_date"
  | "quarter_to_date"
  | "year_to_date"
  | "last_7_days"
  | "last_30_days";

export interface PinnedDateSettings {
  startDate?: string;
  endDate?: string;
  preset?: DatePresetType;
}

// endpoints
export type GetMembersResult = Member[];

export async function getMembers(clientId: string, query: string) {
  const url = `/checkin/search/${query.trim()}/?source=CP&clientId=${clientId}`;
  const response = await userAxios.get<GetMembersResult>(url);
  return response.data;
}

export type GetMemberResult = V2Member | null;

export async function getUserProfileByUUID(
  uuid: string,
  clientId: string,
): Promise<GetMemberResult> {
  const url = `/profiles?user_uuid=${uuid}&client_uuid=${clientId}`;
  const response = await userAxios.get<{ data: V2Member[] }>(url);
  const data = response.data;

  if (data.data.length > 0) {
    return data.data[0];
  }
  return null;
}

export async function getQuickActions(): Promise<QuickAction[]> {
  const url = `/preferences/quick-actions`;
  const response = await userAxios.get<{ data: QuickAction[] }>(url);
  return response.data.data;
}

export async function getQuickActionsList(): Promise<QuickAction[]> {
  const url = `/preferences/quick-actions/list`;
  const response = await userAxios.get<{ data: QuickAction[] }>(url);
  return response.data.data;
}

export interface ToggleQuickActionParams {
  uuid: string;
  operation: "add" | "remove";
}

export async function toggleQuickAction(
  params: ToggleQuickActionParams,
): Promise<{ data: string }> {
  const url = `/preferences/quick-actions`;
  const response = await userAxios.post<{ data: string }>(url, params);
  return response.data;
}

export interface UpdateQuickActionPositionsParams {
  actions: Array<{ uuid: string; position: number }>;
}

export async function updateQuickActionPositions(
  params: UpdateQuickActionPositionsParams,
): Promise<QuickActionBatchUpdateResponse> {
  const url = `/preferences/quick-actions/batch`;
  const response = await userAxios.patch<QuickActionBatchUpdateResponse>(
    url,
    params,
  );
  return response.data;
}

export async function resetQuickActions() {
  const url = `/preferences/quick-actions`;
  const response = await userAxios.delete<{ data: string }>(url);
  return response.data;
}

export async function pinDateSettings(
  settings: PinnedDateSettings,
): Promise<void> {
  const url = `/preferences/pinned-date`;
  await userAxios.post(url, settings);
}

export async function getPinnedDateSettings(): Promise<PinnedDateSettings> {
  const url = `/preferences/pinned-date`;
  const response = await userAxios.get<{ data: PinnedDateSettings }>(url);
  return response.data.data;
}
